import {
	booleanAttribute,
	ChangeDetectionStrategy,
	Component,
	effect,
	ElementRef,
	input,
	Input,
	OnDestroy,
	output,
	signal,
	viewChild,
	WritableSignal,
} from '@angular/core'

// export function delayedSignal<T>(source: Signal<boolean>, delay: Millisecond = 0): Signal<T> {
// 	const delayedSignal = signal(source())
// 	effect(
// 		(onCleanup) => {
// 			const value = source()
// 			if (value === true) {
//
// 			}
// 			const timeout = setTimeout(() => delayedSignal.set(value), delay)
//
// 			// The `onCleanup` argument is a function which is called when the effect
// 			// runs again (and when it is destroyed).
// 			// By clearing the timeout here we achieve proper debouncing.
// 			// See https://angular.io/guide/signals#effect-cleanup-functions
// 			onCleanup(() => clearTimeout(timeout))
// 		},
// 		{ allowSignalWrites: true },
// 	)
// 	return delayedSignal
// }

// export function debouncedSignal<T>(
// 	sourceSignal: Signal<T>,
// 	debounceTimeInMs = 0
// ): Signal<T> {
// 	const debounceSignal = signal(sourceSignal());
// 	effect(
// 		(onCleanup) => {
// 			const value = sourceSignal();
// 			const timeout = setTimeout(
// 				() => debounceSignal.set(value),
// 				debounceTimeInMs
// 			);
//
// 			// The `onCleanup` argument is a function which is called when the effect
// 			// runs again (and when it is destroyed).
// 			// By clearing the timeout here we achieve proper debouncing.
// 			// See https://angular.io/guide/signals#effect-cleanup-functions
// 			onCleanup(() => clearTimeout(timeout));
// 		},
// 		{ allowSignalWrites: true }
// 	);
// 	return debounceSignal;
// }

/**
 * `mos-modal` is a reusable Angular component that represents a modal dialog.
 * It provides a set of events and properties to control its state.
 *
 * Properties:
 * - `mandatory`: A boolean attribute that prevents closing the dialog with the 'escape' key when set to true.
 * - `opened`: A boolean attribute that controls the visibility of the modal. Set to true to open the modal, and false to close it.
 *
 * Events:
 * - `mosOpen`: Emitted when the modal starts opening.
 * - `mosOpened`: Emitted when the modal has fully opened.
 * - `mosClose`: Emitted when the modal starts closing.
 * - `mosClosed`: Emitted when the modal has fully closed.
 * - `openedChange`: Emitted when the `opened` property changes.
 *
 * @example
 * ```html
 * <mos-modal [(opened)]="showModal" (mosOpen)="onModalOpen()" (mosClose)="onModalClose()" #modal>
 *   <!-- Condition to trigger :enter animations -->
 * 	 @if (modal.shouldDisplayContent()) {
 *      <h1>Modal Title</h1>
 *      <p>Modal content...</p>
 *   }
 * </mos-modal>
 **/
@Component({
	selector: 'mos-modal',
	templateUrl: './modal.component.html',
	styleUrl: './modal.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalComponent implements OnDestroy {
	mandatory = input(false, { transform: booleanAttribute })

	protected show = signal(false)
	protected animation = signal<'close' | 'open'>('close')
	protected animCompliantState = signal(false)
	protected animCompliantEffect = effect((onCleanup) => {
		const value = this.show()
		let timeout = undefined
		if (value) {
			timeout = setTimeout(() => this.animCompliantState.set(true), 300)
		} else {
			this.animCompliantState.set(false)
		}
		onCleanup(() => (timeout ? clearTimeout(timeout) : null))
	})
	public shouldDisplayContent = this.animCompliantState.asReadonly()

	private dialog = viewChild<ElementRef<HTMLDialogElement>>('dialog')
	/* Triggers when defer was loaded AND each time show becomes true */
	onDialogAvailable = effect(() => {
		if (this.dialog()) {
			setTimeout(() => this.finalizeOpen())
		}
	})

	mosOpen = output<void>()
	mosOpened = output<void>()
	mosClose = output<void>()
	mosClosed = output<void>()

	@Input({ transform: booleanAttribute }) set opened(opened: boolean | WritableSignal<boolean>) {
		if (opened === this.show()) {
			return
		}

		opened ? this.open() : this.close()
	}

	openedChange = output<boolean>()

	timeouts: ReturnType<typeof setTimeout>[] = []

	ngOnDestroy() {
		this.timeouts.forEach((id) => clearTimeout(id))
		this.afterClose()
	}

	open() {
		this.show.set(true) // Triggers onDialogAvailable
		this.openedChange.emit(true)
	}

	protected finalizeOpen() {
		// Start showing dialog
		this.dialog()?.nativeElement.showModal()
		this.mosOpen.emit()

		// Start animation after one event loop (so animation is not skipped)
		this.timeouts.push(
			setTimeout(() => {
				this.animation.set('open')
				this.mosOpened.emit()
			}, 0),
		)
	}

	close() {
		// Start animation immediately
		this.animation.set('close')
		this.mosClose.emit()

		// Close after animation
		this.timeouts.push(
			setTimeout(() => {
				this.afterClose()
			}, 300),
		)
	}

	afterClose() {
		this.show.set(false)
		this.openedChange.emit(false)

		this.dialog()?.nativeElement.close()
		this.mosClosed.emit()
	}

	/* Prevents closing the dialog with 'escape' key when mandatory otherwise make it go through the proper process */
	protected escape(event: Event) {
		event.preventDefault()
		if (!this.mandatory()) {
			this.close()
		}
	}
}
